/* ==========================================================================
   VANE - theme.js
   Sin dependencias. Web Components nativos.
   ========================================================================== */

(function () {
  'use strict';

  /* --- Toggle de tema claro/oscuro -------------------------------------- */

  class ThemeToggle extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('button');
      if (!this.btn) return;
      this.btn.addEventListener('click', () => this.toggle());
      this.sync();
    }

    current() {
      const explicit = document.documentElement.getAttribute('data-theme');
      if (explicit) return explicit;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    toggle() {
      const next = this.current() === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try { localStorage.setItem('vane:theme', next); } catch (e) { /* modo privado */ }
      this.sync();
    }

    sync() {
      const isDark = this.current() === 'dark';
      this.btn.setAttribute('aria-pressed', String(isDark));
      this.btn.setAttribute('aria-label', isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro');
    }
  }
  customElements.define('theme-toggle', ThemeToggle);

  /* --- Header: oculta al bajar, muestra al subir ------------------------- */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.threshold = 8;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-stuck', y > 40);

      const delta = y - this.lastY;
      if (Math.abs(delta) > this.threshold) {
        // Nunca esconder si hay un drawer abierto
        const drawerOpen = document.querySelector('.drawer.is-open');
        this.classList.toggle('is-hidden', delta > 0 && y > 200 && !drawerOpen);
        this.lastY = y;
      }
    }
  }
  customElements.define('site-header', SiteHeader);

  /* --- Drawer (menu movil / carrito) ------------------------------------ */

  class SiteDrawer extends HTMLElement {
    connectedCallback() {
      this.scrim = this.querySelector('.drawer__scrim');
      this.panel = this.querySelector('.drawer__panel');

      this.close = this.close.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      if (this.scrim) this.scrim.addEventListener('click', this.close);
      this.querySelectorAll('[data-drawer-close]').forEach((el) =>
        el.addEventListener('click', this.close)
      );

      const id = this.id;
      document.querySelectorAll('[data-drawer-open="' + id + '"]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn);
        })
      );
    }

    open(trigger) {
      this.opener = trigger || null;
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      const focusable = this.panel && this.panel.querySelector(
        'a[href], button:not([disabled]), input, select, textarea'
      );
      if (focusable) focusable.focus();
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onKeydown);
      if (this.opener) this.opener.focus();
    }

    onKeydown(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key !== 'Tab' || !this.panel) return;

      // Trampa de foco dentro del panel
      const items = Array.from(
        this.panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea')
      ).filter((el) => el.offsetParent !== null);
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  }
  customElements.define('site-drawer', SiteDrawer);

  /* --- Selector de variantes -------------------------------------------- */
  /* Lee el JSON de variantes que imprime Liquid y actualiza precio,
     disponibilidad, boton y la URL. El stock viene del admin tal cual. */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (e) {
        console.warn('VariantPicker: JSON de variantes invalido', e);
        return;
      }

      this.form = this.closest('form') || document.querySelector('[data-product-form]');
      this.addEventListener('change', () => this.onChange());
      this.onChange();
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('input:checked')).map((i) => i.value);
    }

    findVariant() {
      const chosen = this.selectedOptions();
      return this.variants.find((v) =>
        v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
      );
    }

    onChange() {
      const variant = this.findVariant();
      this.updateAvailability();

      if (!variant) { this.setButton(false, 'No disponible'); return; }

      const idInput = this.form && this.form.querySelector('[name="id"]');
      if (idInput) idInput.value = variant.id;

      const priceEl = document.querySelector('[data-price]');
      if (priceEl && variant.price_html) priceEl.innerHTML = variant.price_html;

      // La linea de stock y el texto del boton los calculo Liquid por variante,
      // asi que aqui no se duplica ninguna regla de inventario.
      const stockEl = document.querySelector('[data-stock-line]');
      if (stockEl && variant.stock_html) stockEl.innerHTML = variant.stock_html;

      this.setButton(variant.available, variant.button_label || 'Anadir a la cesta');

      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    /* Tacha las combinaciones que no existen o estan sin stock */
    updateAvailability() {
      const chosen = this.selectedOptions();
      this.querySelectorAll('.variant-group').forEach((group, groupIndex) => {
        group.querySelectorAll('input').forEach((input) => {
          const test = chosen.slice();
          test[groupIndex] = input.value;
          const match = this.variants.find((v) =>
            v.options.every((o, i) => (i === groupIndex ? o === input.value : o === test[i]))
          );
          input.disabled = !match || !match.available;
        });
      });
    }

    setButton(enabled, label) {
      const btn = this.form && this.form.querySelector('[data-add-to-cart]');
      if (!btn) return;
      btn.disabled = !enabled;
      const text = btn.querySelector('[data-add-label]') || btn;
      text.textContent = label;
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* --- Anadir a la cesta (AJAX) ----------------------------------------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      e.preventDefault();
      const btn = this.form.querySelector('[data-add-to-cart]');
      const label = btn && (btn.querySelector('[data-add-label]') || btn);
      const original = label ? label.textContent : '';

      if (btn) btn.disabled = true;
      if (label) label.textContent = 'Anadiendo...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(this.form.querySelector('[name="id"]').value),
              quantity: Number((this.form.querySelector('[name="quantity"]') || {}).value || 1)
            }]
          })
        });

        const json = await res.json();

        if (!res.ok) {
          // Shopify devuelve description cuando no hay stock suficiente
          throw new Error(json.description || json.message || 'No se pudo anadir el producto');
        }

        await refreshCartCount();
        if (label) label.textContent = 'Anadido';
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
        setTimeout(() => { if (label) label.textContent = original; }, 1600);

      } catch (err) {
        this.showError(err.message);
        if (label) label.textContent = original;
      } finally {
        if (btn) btn.disabled = false;
      }
    }

    showError(msg) {
      let box = this.querySelector('.form-error');
      if (!box) {
        box = document.createElement('p');
        box.className = 'form-error';
        box.setAttribute('role', 'alert');
        this.form.appendChild(box);
      }
      box.textContent = msg;
    }
  }
  customElements.define('product-form', ProductForm);

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (e) {
      // Sin conexion: dejamos el contador como estaba en el render del servidor
    }
  }

  /* --- Colores hermanos -------------------------------------------------- */
  /* El catalogo tiene un producto por color. Se buscan los que comparten
     nombre de prenda con el buscador propio de la tienda (endpoint
     publico, sin claves) y se ofrecen como opciones de color. */

  class ColorSiblings extends HTMLElement {
    async connectedCallback() {
      this.base = this.dataset.base;
      this.current = this.dataset.current;
      this.list = this.querySelector('[data-color-options]');
      if (!this.base || !this.list) return;

      try {
        const url = '/search/suggest.json'
          + '?q=' + encodeURIComponent(this.base)
          + '&resources[type]=product'
          + '&resources[limit]=10';

        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;

        const data = await res.json();
        const found = (data.resources && data.resources.results && data.resources.results.products) || [];

        const siblings = found.filter((p) => {
          if (p.handle === this.current) return false;
          return this.baseOf(p.title) === this.base;
        });

        if (!siblings.length) return;
        siblings.forEach((p) => this.list.appendChild(this.buildOption(p)));
        this.hydrateNames();

      } catch (e) {
        // Sin red o endpoint caido: se queda solo el color actual,
        // que es exactamente lo que ya renderizo el servidor.
      }
    }

    // Mismo criterio que snippets/product-base.liquid
    baseOf(title) {
      return String(title).split('–')[0].split('—')[0].split('-')[0].trim();
    }

    colorOf(title) {
      const t = String(title);
      for (const sep of ['–', '—', '-']) {
        if (t.indexOf(sep) !== -1) return t.split(sep).pop().trim();
      }
      return '';
    }

    buildOption(p) {
      const a = document.createElement('a');
      a.className = 'color-option';
      a.href = p.url;
      const color = this.colorOf(p.title);
      a.title = color || p.title;

      if (p.featured_image && p.featured_image.url) {
        const img = document.createElement('img');
        // El endpoint devuelve la imagen original: se pide recortada
        img.src = p.featured_image.url.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2');
        img.alt = color || p.title;
        img.width = 160;
        img.height = 200;
        img.loading = 'lazy';
        a.appendChild(img);
      } else {
        a.textContent = color;
      }
      return a;
    }

    // Al pasar el raton, el nombre del color de arriba refleja cual se mira
    hydrateNames() {
      const label = this.querySelector('[data-color-name]');
      if (!label) return;
      const original = label.textContent;

      this.list.addEventListener('mouseover', (e) => {
        const opt = e.target.closest('.color-option');
        if (opt && opt.title) label.textContent = opt.title;
      });
      this.list.addEventListener('mouseleave', () => { label.textContent = original; });
    }
  }
  customElements.define('color-siblings', ColorSiblings);

  /* --- Carrusel de productos -------------------------------------------- */

  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      const head = this.closest('.section');
      this.prev = head && head.querySelector('[data-carousel-prev]');
      this.next = head && head.querySelector('[data-carousel-next]');

      if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onScroll);
    }

    // Avanza tantos productos como quepan enteros en pantalla
    scrollByPage(dir) {
      const item = this.track.firstElementChild;
      if (!item) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = item.getBoundingClientRect().width + gap;
      const perView = Math.max(1, Math.floor(this.track.clientWidth / step));
      this.track.scrollBy({ left: dir * step * perView, behavior: 'smooth' });
    }

    onScroll() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= max - 2;
    }
  }
  customElements.define('product-carousel', ProductCarousel);

  /* --- Favoritos --------------------------------------------------------- */
  /* Solo en el navegador de quien lo pulsa: no hay cuenta ni backend,
     que es justo lo que se pidio. Se pierde al limpiar el navegador. */

  const WISH_KEY = 'vane:wishlist';

  function readWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(list) {
    try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); } catch (e) { /* modo privado */ }
  }

  const wishButtons = document.querySelectorAll('.card__wish');
  if (wishButtons.length) {
    const saved = readWishlist();
    wishButtons.forEach((btn) => {
      const card = btn.closest('.card');
      const link = card && card.querySelector('.card__link, .card__title a');
      const id = link ? link.getAttribute('href') : null;
      if (!id) return;

      if (saved.indexOf(id) !== -1) btn.setAttribute('aria-pressed', 'true');

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const list = readWishlist();
        const i = list.indexOf(id);
        if (i === -1) { list.push(id); btn.setAttribute('aria-pressed', 'true'); }
        else { list.splice(i, 1); btn.setAttribute('aria-pressed', 'false'); }
        writeWishlist(list);
      });
    });
  }

  /* --- Aparicion al hacer scroll ---------------------------------------- */

  const reveals = document.querySelectorAll('[data-reveal]');
  if (reveals.length) {
    if (!('IntersectionObserver' in window)) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      // Escalona por posicion dentro del contenedor: una rejilla de productos
      // entra en cascada en vez de aparecer toda de golpe.
      const STEP = 70;   // ms entre elementos
      const MAX = 420;   // tope, para que el ultimo no se haga esperar

      const counters = new Map();
      reveals.forEach((el) => {
        const parent = el.parentElement;
        const i = counters.get(parent) || 0;
        counters.set(parent, i + 1);
        el.style.setProperty('--reveal-delay', Math.min(i * STEP, MAX) + 'ms');
      });

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

      reveals.forEach((el) => io.observe(el));
    }
  }

  /* --- Video del hero: pausa si el usuario prefiere menos movimiento ----- */

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.querySelectorAll('[data-hero-video]').forEach((video) => {
    if (reduceMotion.matches) { video.pause(); video.removeAttribute('autoplay'); }

    const mute = document.querySelector('[data-hero-mute]');
    if (mute) {
      mute.addEventListener('click', () => {
        video.muted = !video.muted;
        mute.setAttribute('aria-pressed', String(!video.muted));
        mute.querySelector('[data-mute-label]').textContent = video.muted ? 'Sonido' : 'Silencio';
      });
    }

    // Ahorra bateria y datos cuando el hero no esta en pantalla
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !reduceMotion.matches) {
            video.play().catch(() => { /* autoplay bloqueado, no pasa nada */ });
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.15 }).observe(video);
    }
  });

  /* --- Marquee: duplica el contenido para un bucle continuo ------------- */

  document.querySelectorAll('.marquee__track').forEach((track) => {
    track.innerHTML += track.innerHTML;
  });

})();
